import React from 'react';
import {
    BarChart, Bar, PieChart, Pie, Cell,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';
import styles from './ChartRenderer.module.css';

const COLORS = ['#00d9ff', '#7c3aed', '#ec4899', '#f59e0b', '#10b981', '#3b82f6', '#ef4444'];

export default function ChartRenderer({ data, type, nameKey = 'name' }) {
    if (!data || !data.length) {
        return <div className={styles.empty}>لا توجد بيانات</div>;
    }

    const normalized = data.map((item) => ({
        ...item,
        name: item[nameKey] ?? item.name ?? item.orientation ?? item.type ?? '—',
    }));

    if (type === 'table') {
        return (
            <table className={styles.table}>
                <thead>
                    <tr>
                        <th className={styles.th}>العنصر</th>
                        <th className={styles.th}>العدد</th>
                    </tr>
                </thead>
                <tbody>
                    {normalized.map((item, idx) => (
                        <tr key={idx} className={styles.tr}>
                            <td className={styles.td}>{item.name || 'غير محدد'}</td>
                            <td className={styles.td}>{item.count?.toLocaleString('ar-SY')}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        );
    }

    if (type === 'bar') {
        return (
            <ResponsiveContainer width="100%" height={320}>
                <BarChart data={normalized} margin={{ top: 10, right: 20, left: 0, bottom: 60 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis
                        dataKey="name"
                        tick={{ fill: '#94a3b8', fontSize: 11 }}
                        angle={-35}
                        textAnchor="end"
                        interval={0}
                    />
                    <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                    <Tooltip
                        contentStyle={{ background: '#1e2a3a', border: '1px solid #334155', borderRadius: 8 }}
                        labelStyle={{ color: '#e2e8f0' }}
                        itemStyle={{ color: '#00d9ff' }}
                    />
                    <Bar dataKey="count" fill={COLORS[0]} radius={[6, 6, 0, 0]} />
                </BarChart>
            </ResponsiveContainer>
        );
    }

    if (type === 'pie') {
        return (
            <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                    <Pie
                        data={normalized}
                        cx="50%"
                        cy="50%"
                        outerRadius={110}
                        dataKey="count"
                        label={({ name, percent }) =>
                            `${name}: ${(percent * 100).toFixed(0)}%`
                        }
                        labelLine={{ stroke: '#475569' }}
                    >
                        {normalized.map((_, index) => (
                            <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                    </Pie>
                    <Tooltip
                        contentStyle={{ background: '#1e2a3a', border: '1px solid #334155', borderRadius: 8 }}
                        itemStyle={{ color: '#e2e8f0' }}
                    />
                </PieChart>
            </ResponsiveContainer>
        );
    }

    if (type === 'cloud') {
        const maxCount = Math.max(...normalized.map((d) => d.count));
        return (
            <div className={styles.tagCloud}>
                {normalized.map((item, idx) => {
                    const size = 12 + Math.round((item.count / maxCount) * 22);
                    return (
                        <span
                            key={idx}
                            className={styles.tag}
                            style={{ fontSize: size, color: COLORS[idx % COLORS.length] }}
                        >
                            {item.name}
                            <span className={styles.tagCount}>{item.count}</span>
                        </span>
                    );
                })}
            </div>
        );
    }

    return null;
}
